import {
  booleanAttribute,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  Output,
} from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';
import { IonButton } from '@ionic/angular/standalone';

@Component({
  selector: 'crowdctrl-nav-lock-button',
  standalone: true,
  imports: [IonButton],
  templateUrl: './nav-lock-button.component.html',
  styleUrl: './nav-lock-button.component.css',
})
export class NavLockButtonComponent {
  /** Toolbar projection marker for `crowdctrl-tab-page` (see {@link ButtonComponent#toolbarButtons}). */
  @Input({ transform: booleanAttribute }) toolbarButtons = false;

  @Input() disabled = false;
  @Input() type: 'button' | 'submit' | 'reset' = 'button';
  @Input() expand?: 'block' | 'full';
  @Input() fill?: 'clear' | 'outline' | 'solid' | 'default';
  @Input() color?: string;
  @Input() size?: 'small' | 'default' | 'large';
  @Input() shape?: 'round';
  @Input() strong?: boolean;
  @Input() routerDirection?: 'forward' | 'back' | 'root';
  @Input() navLockResetToken: unknown;
  @Input() navigateTo: string | any[] | null = null;
  @Input() navigationExtras?: NavigationExtras;

  @Output() navigationFailed = new EventEmitter<unknown>();
  @Output() navigationStarted = new EventEmitter<void>();
  @Output() navigationSucceeded = new EventEmitter<void>();

  private lastResetToken: unknown;
  private navigating = false;

  constructor(private readonly router: Router) {}

  @HostBinding('class.expand-block')
  get hasExpandBlock(): boolean {
    return this.expand === 'block';
  }

  @HostBinding('attr.toolbarbuttons')
  get toolbarButtonsHostAttr(): string | null {
    return this.toolbarButtons ? '' : null;
  }

  get isDisabled(): boolean {
    this.resetIfNeeded();
    return this.disabled || this.navigating;
  }

  async onClick(): Promise<void> {
    this.resetIfNeeded();
    if (this.disabled || this.navigating || !this.navigateTo) {
      return;
    }
    this.navigating = true;
    this.navigationStarted.emit();
    try {
      const ok = Array.isArray(this.navigateTo)
        ? await this.router.navigate(this.navigateTo, this.navigationExtras)
        : await this.router.navigateByUrl(this.navigateTo, this.navigationExtras);
      if (!ok) {
        this.navigating = false;
        this.navigationFailed.emit(new Error('Navigation returned false.'));
        return;
      }
      this.navigationSucceeded.emit();
    } catch (error) {
      this.navigating = false;
      this.navigationFailed.emit(error);
    }
  }

  private resetIfNeeded(): void {
    if (this.navLockResetToken !== this.lastResetToken) {
      this.lastResetToken = this.navLockResetToken;
      this.navigating = false;
    }
  }
}
